import React, {
	createContext,
	useContext,
	useState,
	useCallback,
	useMemo,
	ReactNode,
	useEffect,
} from 'react';
import { FeatureCode } from '../common/constants/featureCodes';
import { FeatureWithUsage, FeatureCheckResult, FeatureUsageInfo } from '../type/feature-types';

/**
 * Feature context value interface
 */
export interface FeatureContextValue {
	/** List of features with their current status and usage */
	features: FeatureWithUsage[];
	/** User's current plan type */
	planType: string | null;
	/** Whether features are being loaded */
	isLoading: boolean;

	// Helper methods
	/** Get a feature by its code */
	getFeature: (code: FeatureCode | string) => FeatureWithUsage | undefined;
	/** Check if a feature can be used (enabled and within limits) */
	canUse: (code: FeatureCode | string) => boolean;
	/** Get detailed check result for a feature */
	checkFeature: (code: FeatureCode | string) => FeatureCheckResult;
	/** Get usage info for a limited feature */
	getUsage: (code: FeatureCode | string) => FeatureUsageInfo | null;

	// Actions
	/** Update features from auth sync response */
	setFeatures: (features: FeatureWithUsage[], planType: string | null) => void;
	/** Optimistically increment local usage for a feature */
	incrementLocalUsage: (code: FeatureCode | string) => void;
	/** Clear all features (on logout) */
	clearFeatures: () => void;
}

const defaultContextValue: FeatureContextValue = {
	features: [],
	planType: null,
	isLoading: true,
	getFeature: () => undefined,
	canUse: () => false,
	checkFeature: () => ({ allowed: false, reason: 'disabled' }),
	getUsage: () => null,
	setFeatures: () => {},
	incrementLocalUsage: () => {},
	clearFeatures: () => {},
};

export const FeatureContext = createContext<FeatureContextValue>(defaultContextValue);

interface FeatureProviderProps {
	children: ReactNode;
}

/**
 * Provider component for feature gating context.
 * Wrap your app with this to enable feature checks.
 */
export const FeatureProvider: React.FC<FeatureProviderProps> = ({ children }) => {
	const [features, setFeaturesState] = useState<FeatureWithUsage[]>([]);
	const [planType, setPlanType] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	// Listen for auth sync events to update features
	useEffect(() => {
		const handleAuthSync = (
			event: CustomEvent<{ features: FeatureWithUsage[]; planType: string | null }>,
		) => {
			if (event.detail.features) {
				setFeaturesState(event.detail.features);
				setPlanType(event.detail.planType);
				setIsLoading(false);
			}
		};

		window.addEventListener('auth-features-synced', handleAuthSync as EventListener);

		return () => {
			window.removeEventListener('auth-features-synced', handleAuthSync as EventListener);
		};
	}, []);

	/**
	 * Get a feature by its code
	 */
	const getFeature = useCallback(
		(code: FeatureCode | string): FeatureWithUsage | undefined => {
			return features.find((f) => f.code === code);
		},
		[features],
	);

	/**
	 * Check if a feature can be used
	 */
	const canUse = useCallback(
		(code: FeatureCode | string): boolean => {
			const feature = features.find((f) => f.code === code);
			if (!feature) return false;
			if (!feature.isEnabled) return false;

			// For limit features, check if there's remaining usage
			if (feature.type === 'limit') {
				// null remaining means unlimited
				if (feature.remaining === null) return true;
				return feature.remaining > 0;
			}

			return true;
		},
		[features],
	);

	/**
	 * Get detailed check result for a feature
	 */
	const checkFeature = useCallback(
		(code: FeatureCode | string): FeatureCheckResult => {
			const feature = features.find((f) => f.code === code);

			if (!feature) {
				return {
					allowed: false,
					reason: 'disabled',
					message: 'Feature not found',
				};
			}

			if (!feature.isEnabled) {
				return {
					allowed: false,
					reason: 'disabled',
					message: `${feature.name} is not available on your current plan`,
				};
			}

			// For limit features, check if there's remaining usage
			if (feature.type === 'limit') {
				if (feature.remaining !== null && feature.remaining <= 0) {
					return {
						allowed: false,
						reason: 'limit_reached',
						message: `You've used all your ${feature.name} for this month`,
					};
				}
			}

			return { allowed: true };
		},
		[features],
	);

	/**
	 * Get usage info for a limited feature
	 */
	const getUsage = useCallback(
		(code: FeatureCode | string): FeatureUsageInfo | null => {
			const feature = features.find((f) => f.code === code);
			if (!feature || feature.type !== 'limit') return null;

			return {
				used: feature.used ?? 0,
				limit: feature.limit,
				remaining: feature.remaining,
			};
		},
		[features],
	);

	/**
	 * Update features from auth sync response
	 */
	const setFeatures = useCallback(
		(newFeatures: FeatureWithUsage[], newPlanType: string | null) => {
			setFeaturesState(newFeatures);
			setPlanType(newPlanType);
			setIsLoading(false);
		},
		[],
	);

	/**
	 * Optimistically increment local usage for a feature
	 * Call this after a successful action to update UI immediately
	 */
	const incrementLocalUsage = useCallback((code: FeatureCode | string) => {
		setFeaturesState((prev) =>
			prev.map((f) => {
				if (f.code !== code || f.type !== 'limit') return f;
				return {
					...f,
					used: (f.used ?? 0) + 1,
					remaining: f.remaining !== null ? f.remaining - 1 : null,
				};
			}),
		);
	}, []);

	/**
	 * Clear all features (on logout)
	 */
	const clearFeatures = useCallback(() => {
		setFeaturesState([]);
		setPlanType(null);
		setIsLoading(true);
	}, []);

	const value = useMemo<FeatureContextValue>(
		() => ({
			features,
			planType,
			isLoading,
			getFeature,
			canUse,
			checkFeature,
			getUsage,
			setFeatures,
			incrementLocalUsage,
			clearFeatures,
		}),
		[
			features,
			planType,
			isLoading,
			getFeature,
			canUse,
			checkFeature,
			getUsage,
			setFeatures,
			incrementLocalUsage,
			clearFeatures,
		],
	);

	return <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>;
};

/**
 * Hook to access the feature context
 */
export const useFeatureContext = (): FeatureContextValue => {
	const context = useContext(FeatureContext);
	if (!context) {
		throw new Error('useFeatureContext must be used within a FeatureProvider');
	}
	return context;
};

export default FeatureContext;
